import { buildChart, chartIntervalForRange } from './chart';
import type { VolumeProfileChart } from './chart';
import {
    autoIncrement,
    bucketLevels,
    EMPTY_VOLUME_PROFILE_LEVELS,
    filterSession,
    fromKiasiProfile,
    keyLevels,
    liveBarInWindow,
    mergeOpenBar,
    VOLUME_PROFILE_INCREMENTS,
} from './snapshot';
import type {
    VolumeProfileIncrement,
    VolumeProfileIncrementValue,
    VolumeProfileKeyLevels,
    VolumeProfileLevel,
    VolumeProfileSnapshot,
} from './snapshot';
import {
    historyFailed,
    historyLoaded,
    historyLoading,
    liveBarUpdated,
    liveProfileChanged,
    liveStatusChanged,
    setIncrement,
    setRange,
    setSession,
    setTab,
    setViewMode,
    subjectChanged,
    summaryLoaded,
    toggleOverlay,
} from './state/actions';
import type { VolumeProfileAction } from './state/actions';
import { reducer } from './state/reducer';
import { createInitialVolumeProfileState } from './state/types';
import type {
    VolumeProfileBarSegment,
    VolumeProfileLegendItem,
    VolumeProfileLevelView,
    VolumeProfileOverlay,
    VolumeProfileRange,
    VolumeProfileSession,
    VolumeProfileStatGroup,
    VolumeProfileStatRow,
    VolumeProfileState,
    VolumeProfileTabId,
    VolumeProfileViewMode,
} from './state/types';
import { resolveVolumeProfileWindow } from './window';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { isAxiosError } from 'axios';
import type { AxiosInstance } from 'axios';
import numbro from 'numbro';
import type { KiasiHistorySummaryReadReport, KiasiVolumeProfile } from 'src/lib/kiasi';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getVolumeBarsSummaryQuery, getVolumeProfileQuery } from 'src/queries/volumeProfile';
import type { KiasiLiveBarService, LiveBarStatus, LiveBarSubscription } from 'src/services/KiasiLiveBarService';
import type { ReactBindings } from 'src/types/bindings';

const EMPTY_PROFILE_ROWS: readonly VolumeProfileLevelView[] = Object.freeze([]);
const EMPTY_STATS: readonly VolumeProfileStatGroup[] = Object.freeze([]);

/**
 * History changes all day for every range (even `custom`, which is today's
 * regular session). While the live stream is connected every close triggers a
 * refetch; polling covers the stream being unavailable or down.
 */
export const VOLUME_PROFILE_POLL_INTERVAL_MS = 10_000;

export const VOLUME_PROFILE_UNAVAILABLE_MESSAGE = 'Volume profile data is not available in this environment.';

export interface VolumeProfileIncrementOption {
    readonly id: string;
    readonly value: VolumeProfileIncrement;
    readonly label: string;
}

export interface VolumeProfileModelInit {
    readonly symbol: ReadonlySignal<string>;
    readonly enabled?: ReadonlySignal<boolean>;
    readonly lastPrice?: ReadonlySignal<number | null>;
    readonly kiasiApi: AxiosInstance;
    /** Optional live open-bar stream; without it the model relies on polling alone. */
    readonly liveBarService?: KiasiLiveBarService;
    /** Developers see the stream's delivery profile; defaults to `false`. */
    readonly isDeveloper?: ReadonlySignal<boolean>;
    readonly queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    /** Clock for resolving sliding windows; injectable for tests. */
    readonly now?: () => number;
}

type Tone = VolumeProfileStatRow['tone'];

function formatPrice(value: number): string {
    return numbro(value).formatCurrency({ currencySymbol: '$', mantissa: Math.abs(value) < 1 ? 4 : 2 });
}

function formatOptionalPrice(value: number | null): string {
    return value === null ? '-' : formatPrice(value);
}

function formatVolume(value: number): string {
    // numbro's average format turns 500 into "0.5K"; keep sub-thousand volumes plain.
    if (Math.abs(value) < 1_000) return numbro(value).format({ mantissa: 0 });
    return numbro(value).format({ average: true, mantissa: 1 }).toUpperCase();
}

function formatPercent(value: number): string {
    return numbro(value).format({ output: 'percent', mantissa: 1 });
}

function formatIncrement(value: VolumeProfileIncrementValue): string {
    return numbro(value).formatCurrency({ currencySymbol: '$', mantissa: value < 1 ? 2 : 0, optionalMantissa: true });
}

function progress(value: number, base: number): number {
    return base > 0 ? Math.min(100, Math.max(0, (value / base) * 100)) : 0;
}

function toneClass(tone: Tone): string {
    switch (tone) {
        case 'aqua':
            return 'bg-aqua-500';
        case 'jade':
            return 'bg-jade-450';
        case 'ember':
            return 'bg-ember-450';
        case 'honey':
            return 'bg-honey-450';
        case 'purple':
            return 'bg-purple-500';
        case 'default':
            return 'bg-gray-400';
        default: {
            const _exhaustiveCheck: never = tone;
            return 'bg-gray-400';
        }
    }
}

function statRow(
    label: string,
    value: string,
    tone: Tone = 'default',
    pct: number | null = null,
): VolumeProfileStatRow {
    return { label, value, tone, progressPct: pct, progressClassName: toneClass(tone) };
}

function buildSegments(
    viewMode: VolumeProfileViewMode,
    level: VolumeProfileLevel,
    maxTotal: number,
): readonly VolumeProfileBarSegment[] {
    switch (viewMode) {
        case 'quote':
            return [
                { id: 'bid', label: 'Bid', widthPct: progress(level.bid, maxTotal), className: 'bg-ember-450/60' },
                { id: 'mid', label: 'Mid', widthPct: progress(level.mid, maxTotal), className: 'bg-purple-500/55' },
                { id: 'ask', label: 'Ask', widthPct: progress(level.ask, maxTotal), className: 'bg-jade-450/60' },
            ];
        case 'session':
            return [
                {
                    id: 'premarket',
                    label: 'Pre',
                    widthPct: progress(level.premarket, maxTotal),
                    className: 'bg-honey-450/65',
                },
                {
                    id: 'regular',
                    label: 'Reg',
                    widthPct: progress(level.regular, maxTotal),
                    className: 'bg-aqua-500/60',
                },
                {
                    id: 'postmarket',
                    label: 'Post',
                    widthPct: progress(level.postmarket, maxTotal),
                    className: 'bg-purple-500/55',
                },
            ];
        case 'total':
            return [
                {
                    id: 'total',
                    label: 'Total',
                    widthPct: progress(level.total, maxTotal),
                    className: 'bg-honey-450/65',
                },
            ];
        default: {
            const _exhaustiveCheck: never = viewMode;
            return [];
        }
    }
}

function buildLegend(tabId: VolumeProfileTabId, viewMode: VolumeProfileViewMode): readonly VolumeProfileLegendItem[] {
    const base =
        tabId === 'chart'
            ? [
                  { id: 'bid', label: 'Bid', className: 'bg-ember-450/60' },
                  { id: 'ask', label: 'Ask', className: 'bg-jade-450/60' },
              ]
            : viewMode === 'quote'
              ? [
                    { id: 'bid', label: 'Bid', className: 'bg-ember-450/60' },
                    { id: 'mid', label: 'Mid', className: 'bg-purple-500/55' },
                    { id: 'ask', label: 'Ask', className: 'bg-jade-450/60' },
                ]
              : viewMode === 'session'
                ? [
                      { id: 'premarket', label: 'Pre', className: 'bg-honey-450/65' },
                      { id: 'regular', label: 'Reg', className: 'bg-aqua-500/60' },
                      { id: 'postmarket', label: 'Post', className: 'bg-purple-500/55' },
                  ]
                : [{ id: 'total', label: 'Total', className: 'bg-honey-450/65' }];
    return [
        ...base,
        { id: 'poc', label: 'POC', className: 'bg-honey-450' },
        { id: 'value-area', label: 'Value Area', className: 'bg-aqua-500/60' },
    ];
}

interface SessionTotals {
    readonly total: number;
    readonly bid: number;
    readonly ask: number;
    readonly mid: number;
    readonly premarket: number;
    readonly regular: number;
    readonly postmarket: number;
}

function sumLevels(levels: readonly VolumeProfileLevel[]): SessionTotals {
    let total = 0;
    let bid = 0;
    let ask = 0;
    let mid = 0;
    let premarket = 0;
    let regular = 0;
    let postmarket = 0;
    for (const level of levels) {
        total += level.total;
        bid += level.bid;
        ask += level.ask;
        mid += level.mid;
        premarket += level.premarket;
        regular += level.regular;
        postmarket += level.postmarket;
    }
    return { total, bid, ask, mid, premarket, regular, postmarket };
}

function snapshotTotals(snapshot: VolumeProfileSnapshot): SessionTotals {
    return {
        total: snapshot.totalVolume,
        bid: snapshot.bidVolume,
        ask: snapshot.askVolume,
        mid: snapshot.midVolume,
        premarket: snapshot.premarketVolume,
        regular: snapshot.regularVolume,
        postmarket: snapshot.postmarketVolume,
    };
}

interface AuctionTotals {
    readonly open: number;
    readonly close: number;
}

function buildStats(
    totals: SessionTotals,
    keyPrices: { poc: number; vah: number; val: number } | null,
    auctions: AuctionTotals | null,
): readonly VolumeProfileStatGroup[] {
    if (totals.total === 0) return EMPTY_STATS;
    const quoteTotal = totals.bid + totals.mid + totals.ask || 1;
    const sessionTotal = totals.premarket + totals.regular + totals.postmarket || 1;
    const delta = totals.ask - totals.bid;
    const safeTotal = totals.total || 1;

    return [
        ...(keyPrices === null
            ? []
            : [
                  {
                      id: 'levels',
                      title: 'Key Levels',
                      rows: [
                          statRow('POC', formatPrice(keyPrices.poc), 'honey'),
                          statRow('VAH', formatPrice(keyPrices.vah), 'aqua'),
                          statRow('VAL', formatPrice(keyPrices.val), 'aqua'),
                      ],
                  },
              ]),
        {
            id: 'quote',
            title: 'Quote Location',
            rows: [
                statRow('Vol Bid', formatVolume(totals.bid), 'ember', progress(totals.bid, quoteTotal)),
                statRow('Vol Ask', formatVolume(totals.ask), 'jade', progress(totals.ask, quoteTotal)),
                statRow('Vol Mid', formatVolume(totals.mid), 'purple', progress(totals.mid, quoteTotal)),
                statRow('% Bid', formatPercent(totals.bid / quoteTotal), 'ember'),
                statRow('% Ask', formatPercent(totals.ask / quoteTotal), 'jade'),
                statRow('% Mid', formatPercent(totals.mid / quoteTotal), 'purple'),
            ],
        },
        {
            id: 'session',
            title: 'Session Type',
            rows: [
                statRow('Vol Pre', formatVolume(totals.premarket), 'honey', progress(totals.premarket, sessionTotal)),
                statRow('Vol Reg', formatVolume(totals.regular), 'aqua', progress(totals.regular, sessionTotal)),
                statRow(
                    'Vol Post',
                    formatVolume(totals.postmarket),
                    'purple',
                    progress(totals.postmarket, sessionTotal),
                ),
                statRow('% Pre', formatPercent(totals.premarket / sessionTotal), 'honey'),
                statRow('% Reg', formatPercent(totals.regular / sessionTotal), 'aqua'),
                statRow('% Post', formatPercent(totals.postmarket / sessionTotal), 'purple'),
            ],
        },
        ...(auctions === null
            ? []
            : [
                  {
                      id: 'auctions',
                      title: 'Auctions',
                      rows: [
                          statRow(
                              'Open Auction',
                              formatVolume(auctions.open),
                              'honey',
                              progress(auctions.open, safeTotal),
                          ),
                          statRow(
                              'Close Auction',
                              formatVolume(auctions.close),
                              'aqua',
                              progress(auctions.close, safeTotal),
                          ),
                      ],
                  },
              ]),
        {
            id: 'totals',
            title: 'Totals',
            rows: [
                statRow('Total Vol', formatVolume(totals.total), 'default'),
                statRow(
                    'Delta',
                    `${delta >= 0 ? '+' : '-'}${formatVolume(Math.abs(delta))}`,
                    delta >= 0 ? 'jade' : 'ember',
                ),
            ],
        },
    ];
}

function rangeSubtitle(range: VolumeProfileRange): string {
    switch (range) {
        case 'day':
            return 'Current day - full session';
        case '1h':
            return 'Last 60 minutes';
        case '5m':
            return 'Last 5 minutes';
        case '1m':
            return 'Last 1 minute';
        case 'custom':
            return 'Custom range - 09:30 to 16:00 ET';
        default: {
            const _exhaustiveCheck: never = range;
            return '';
        }
    }
}

function describeError(error: Error, symbol: string): string {
    if (isAxiosError(error)) {
        // No response at all: the host is not deployed or not reachable from here.
        if (error.response === undefined) return VOLUME_PROFILE_UNAVAILABLE_MESSAGE;
        switch (error.response.status) {
            case 401:
            case 403:
                return 'Your account is not entitled to volume profile data.';
            case 404:
                return `No volume profile data for ${symbol}.`;
            default:
                break;
        }
    }
    return error.message;
}

export class VolumeProfileModel implements Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #state = signal<VolumeProfileState>(createInitialVolumeProfileState());
    readonly #symbol: ReadonlySignal<string>;
    readonly #lastPrice: ReadonlySignal<number | null>;
    readonly #query: QueryObserverModel<KiasiVolumeProfile, Error, KiasiVolumeProfile>;
    readonly #summaryQuery: QueryObserverModel<KiasiHistorySummaryReadReport, Error, KiasiHistorySummaryReadReport>;
    /** At most one live stream, for the current symbol (PLAT-12347). */
    readonly #liveBox = new DisposableBox<LiveBarSubscription>();
    readonly #liveBar = computed(() => this.#state.value.data.liveBar);
    readonly #liveStatus = computed(() => this.#state.value.data.liveStatus);
    readonly #liveProfileLabel: ReadonlySignal<string | null>;
    readonly #chartInterval = computed(() => chartIntervalForRange(this.#state.value.range));
    readonly #chart: ReadonlySignal<VolumeProfileChart | null>;

    readonly #tabId = computed(() => this.#state.value.tabId);
    readonly #viewMode = computed(() => this.#state.value.viewMode);
    readonly #session = computed(() => this.#state.value.session);
    readonly #range = computed(() => this.#state.value.range);
    readonly #increment = computed(() => this.#state.value.increment);
    readonly #overlays = computed(() => this.#state.value.overlays);
    readonly #rangeSubtitle = computed(() => rangeSubtitle(this.#state.value.range));
    readonly #legend = computed(() => buildLegend(this.#state.value.tabId, this.#state.value.viewMode));

    readonly #snapshot: ReadonlySignal<VolumeProfileSnapshot | null>;
    readonly #sessionLevels: ReadonlySignal<readonly VolumeProfileLevel[]>;
    readonly #effectiveIncrement: ReadonlySignal<VolumeProfileIncrementValue>;
    readonly #bucketedLevels: ReadonlySignal<readonly VolumeProfileLevel[]>;
    readonly #keyLevels: ReadonlySignal<VolumeProfileKeyLevels>;
    readonly #levels: ReadonlySignal<readonly VolumeProfileLevelView[]>;
    readonly #stats: ReadonlySignal<readonly VolumeProfileStatGroup[]>;
    readonly #incrementOptions: ReadonlySignal<readonly VolumeProfileIncrementOption[]>;
    readonly #loading: ReadonlySignal<boolean>;
    readonly #error: ReadonlySignal<string | null>;
    readonly #isEmpty: ReadonlySignal<boolean>;
    readonly #vwap: ReadonlySignal<number | null>;
    readonly #lastPriceFormatted: ReadonlySignal<string>;
    readonly #vwapFormatted: ReadonlySignal<string>;

    constructor(init: VolumeProfileModelInit) {
        this.#symbol = init.symbol;
        this.#lastPrice = init.lastPrice ?? signal(null);
        const enabled = init.enabled ?? signal(true);
        const now = init.now ?? Date.now;
        const api = init.kiasiApi;

        const liveStreaming = computed(() => this.#liveStatus.value === 'streaming');
        const isDeveloper = init.isDeveloper ?? signal(false);
        this.#liveProfileLabel = computed(() => {
            const profile = this.#state.value.data.liveProfile;
            if (!isDeveloper.value || profile === null) return null;
            return profile.cadenceMillis === null ? profile.name : `${profile.name} · ${profile.cadenceMillis}ms`;
        });
        this.#disposableStack.use(this.#liveBox);

        {
            // Engines deliver into the reducer; the reducer decides what is shown. The subject
            // transition clears data from a previous symbol or range before new results arrive.
            let lastProfile: KiasiVolumeProfile | undefined;
            this.#disposableStack.defer(
                effect(() => {
                    const symbol = this.#symbol.value;
                    const range = this.#range.value;
                    untracked(() => {
                        lastProfile = undefined;
                        this.#dispatch(subjectChanged(symbol === '' ? null : { symbol, range }));
                    });
                }),
            );
            const pollInterval = computed(() => (liveStreaming.value ? false : VOLUME_PROFILE_POLL_INTERVAL_MS));
            const queryOptions = computed(() => {
                const range = this.#range.value;
                return getVolumeProfileQuery({
                    api,
                    symbol: this.#symbol.value,
                    rangeKey: range,
                    resolveWindow: () => resolveVolumeProfileWindow(range, now()),
                    // Every profile response carries the side and session splits; the mode only labels it.
                    mode: 'volume',
                    enabled: enabled.value,
                    refetchIntervalMs: pollInterval.value,
                });
            });
            const query = this.#disposableStack.use(
                init.queryObserverModelFactory<KiasiVolumeProfile, Error, KiasiVolumeProfile>(queryOptions),
            );
            this.#query = query;
            this.#disposableStack.use(
                query.subscribe((result) => {
                    if (result.data !== undefined) {
                        // Refetches republish the same object; convert once per distinct payload.
                        if (result.data !== lastProfile) {
                            lastProfile = result.data;
                            this.#dispatch(historyLoaded(fromKiasiProfile(result.data)));
                        }
                    } else {
                        // No data at all: a first load, or QueryCacheUserService reset the cache.
                        lastProfile = undefined;
                        if (result.error !== null) this.#dispatch(historyFailed(result.error));
                        else if (result.isFetching) this.#dispatch(historyLoading());
                    }
                }),
            );

            const summaryOptions = computed(() => {
                const range = this.#range.value;
                return getVolumeBarsSummaryQuery({
                    api,
                    symbol: this.#symbol.value,
                    interval: this.#chartInterval.value,
                    rangeKey: range,
                    resolveWindow: () => resolveVolumeProfileWindow(range, now()),
                    enabled: enabled.value,
                    refetchIntervalMs: pollInterval.value,
                });
            });
            const summaryQuery = this.#disposableStack.use(
                init.queryObserverModelFactory<KiasiHistorySummaryReadReport, Error, KiasiHistorySummaryReadReport>(
                    summaryOptions,
                ),
            );
            this.#summaryQuery = summaryQuery;
            this.#disposableStack.use(
                summaryQuery.subscribe((result) => {
                    this.#dispatch(summaryLoaded(result.data?.rows ?? null));
                }),
            );

            const liveBarService = init.liveBarService;
            if (liveBarService !== undefined) {
                // One stream per symbol; the box tears the previous one down.
                this.#disposableStack.defer(
                    effect(() => {
                        const symbol = this.#symbol.value;
                        const on = enabled.value;
                        untracked(() => {
                            if (!on || symbol === '') {
                                this.#liveBox.clear();
                                this.#dispatch(liveStatusChanged('idle'));
                                this.#dispatch(liveProfileChanged(null));
                                this.#dispatch(liveBarUpdated(null));
                                return;
                            }
                            this.#liveBox.next(liveBarService.subscribe({ symbol, interval: '1m' }));
                        });
                    }),
                );
                // The stream delivers into the reducer like the queries do.
                this.#disposableStack.defer(
                    effect(() => {
                        const symbol = this.#symbol.value;
                        const on = enabled.value;
                        const subscription = untracked(() => (on && symbol !== '' ? this.#liveBox.current : undefined));
                        if (subscription === undefined) return;
                        const bar = subscription.bar.value;
                        const status = subscription.status.value;
                        const profile = subscription.profile.value;
                        untracked(() => {
                            this.#dispatch(liveStatusChanged(status));
                            this.#dispatch(liveProfileChanged(profile));
                            this.#dispatch(liveBarUpdated(bar));
                        });
                    }),
                );
                // A bar's final version landed in history: fetch it rather than wait for the poll.
                this.#disposableStack.defer(
                    effect(() => {
                        const symbol = this.#symbol.value;
                        const subscription = untracked(() => (symbol === '' ? undefined : this.#liveBox.current));
                        if (subscription === undefined || subscription.closedRevision.value === 0) return;
                        untracked(() => {
                            void query.queryObserver.refetch();
                            void summaryQuery.queryObserver.refetch();
                        });
                    }),
                );
            }
        }

        const data = computed(() => this.#state.value.data);
        this.#snapshot = computed(() => {
            const history = data.value.history;
            if (history === null) return null;
            return mergeOpenBar(history, this.#liveBar.value, resolveVolumeProfileWindow(this.#range.value, now()));
        });
        const summaryRows = computed(() => data.value.summaryRows);
        const auctions = computed((): AuctionTotals | null => {
            const rows = summaryRows.value;
            if (rows === null) return null;
            let open = 0;
            let close = 0;
            for (const row of rows) {
                open += row.open_auction_vol;
                close += row.close_auction_vol;
            }
            // Rows are at the chart interval; a live bar of another interval would flicker in and
            // out between its own close and the chart bar's close.
            const live = this.#liveBar.value;
            if (
                live !== null &&
                live.interval === this.#chartInterval.value &&
                liveBarInWindow(live, resolveVolumeProfileWindow(this.#range.value, now()))
            ) {
                open += live.openAuctionVolume;
                close += live.closeAuctionVolume;
            }
            return { open, close };
        });
        // Overlays follow the session filter the way the ladder and sidebar do: a session subset
        // takes POC and value area from the bucketed levels and its side volumes from the session
        // levels. Candles are separately sourced and the window VWAP is not session-scoped.
        const chartProfile = computed((): VolumeProfileSnapshot | null => {
            const snapshot = this.#snapshot.value;
            if (snapshot === null || this.#session.value === 'all') return snapshot;
            const bucketed = this.#bucketedLevels.value;
            const { pocIndex, vahIndex, valIndex } = this.#keyLevels.value;
            const totals = sumLevels(this.#sessionLevels.value);
            return {
                ...snapshot,
                poc: pocIndex === -1 ? snapshot.poc : bucketed[pocIndex].price,
                vah: vahIndex === -1 ? snapshot.vah : bucketed[vahIndex].price,
                val: valIndex === -1 ? snapshot.val : bucketed[valIndex].price,
                totalVolume: totals.total,
                bidVolume: totals.bid,
                askVolume: totals.ask,
                midVolume: totals.mid,
            };
        });
        this.#chart = computed(() => {
            const rows = summaryRows.value;
            if (rows === null) return null;
            const range = this.#range.value;
            return buildChart({
                rows,
                liveBar: this.#liveBar.value,
                interval: this.#chartInterval.value,
                window: resolveVolumeProfileWindow(range, now()),
                profile: chartProfile.value,
                // A session that traded nothing has no POC or value area of its own.
                keyLevels: this.#session.value === 'all' || this.#keyLevels.value.pocIndex !== -1,
                lastPrice: this.#lastPrice.value,
            });
        });
        this.#sessionLevels = computed(() => {
            const snapshot = this.#snapshot.value;
            if (snapshot === null) return EMPTY_VOLUME_PROFILE_LEVELS;
            return filterSession(snapshot.levels, this.#session.value);
        });
        this.#effectiveIncrement = computed(() => {
            const increment = this.#increment.value;
            return increment === 'auto' ? autoIncrement(this.#sessionLevels.value) : increment;
        });
        this.#bucketedLevels = computed(() => bucketLevels(this.#sessionLevels.value, this.#effectiveIncrement.value));
        this.#keyLevels = computed(() => keyLevels(this.#bucketedLevels.value));
        this.#incrementOptions = computed(() => [
            { id: 'auto', value: 'auto', label: `Auto (${formatIncrement(this.#effectiveIncrement.value)})` },
            ...VOLUME_PROFILE_INCREMENTS.map((value) => ({ id: String(value), value, label: formatIncrement(value) })),
        ]);

        this.#levels = computed(() => {
            const levels = this.#bucketedLevels.value;
            if (levels.length === 0) return EMPTY_PROFILE_ROWS;
            const viewMode = this.#viewMode.value;
            const { pocIndex, vahIndex, valIndex } = this.#keyLevels.value;
            const maxTotal = Math.max(...levels.map((level) => level.total), 1);
            const rows = levels.map(
                (level, index): VolumeProfileLevelView => ({
                    id: String(level.price),
                    price: level.price,
                    priceFormatted: formatPrice(level.price),
                    total: level.total,
                    totalFormatted: formatVolume(level.total),
                    segments: buildSegments(viewMode, level, maxTotal),
                    isPoc: index === pocIndex,
                    isVah: index === vahIndex,
                    isVal: index === valIndex,
                }),
            );
            // Highest price on top, as a ladder reads.
            rows.reverse();
            return rows;
        });

        this.#stats = computed(() => {
            const snapshot = this.#snapshot.value;
            if (snapshot === null) return EMPTY_STATS;
            const session = this.#session.value;
            // Kiasi's own POC and value area are exact to the cent; a session subset has to be
            // derived from the bucketed levels, which is also what the ladder highlights.
            const { pocIndex, vahIndex, valIndex } = this.#keyLevels.value;
            const bucketed = this.#bucketedLevels.value;
            const keyPrices =
                session === 'all'
                    ? { poc: snapshot.poc, vah: snapshot.vah, val: snapshot.val }
                    : pocIndex === -1
                      ? null
                      : { poc: bucketed[pocIndex].price, vah: bucketed[vahIndex].price, val: bucketed[valIndex].price };
            const totals = session === 'all' ? snapshotTotals(snapshot) : sumLevels(this.#sessionLevels.value);
            // Auction volumes are not split by session; show them with the whole-session view only.
            return buildStats(totals, keyPrices, session === 'all' ? auctions.value : null);
        });

        this.#loading = computed(() => data.value.historyStatus === 'loading' && data.value.history === null);
        this.#error = computed(() => {
            const { historyError, history } = data.value;
            if (historyError === null || history !== null) return null;
            return describeError(historyError, this.#symbol.value);
        });
        this.#isEmpty = computed(() => !this.#loading.value && this.#levels.value.length === 0);
        this.#vwap = computed(() => this.#snapshot.value?.vwap ?? null);
        this.#lastPriceFormatted = computed(() => formatOptionalPrice(this.#lastPrice.value));
        this.#vwapFormatted = computed(() => formatOptionalPrice(this.#vwap.value));
    }

    #dispatch(action: VolumeProfileAction): void {
        this.#state.value = reducer(this.#state.value, action);
    }

    get tabId(): ReadonlySignal<VolumeProfileTabId> {
        return this.#tabId;
    }

    get viewMode(): ReadonlySignal<VolumeProfileViewMode> {
        return this.#viewMode;
    }

    get session(): ReadonlySignal<VolumeProfileSession> {
        return this.#session;
    }

    get range(): ReadonlySignal<VolumeProfileRange> {
        return this.#range;
    }

    get increment(): ReadonlySignal<VolumeProfileIncrement> {
        return this.#increment;
    }

    get incrementOptions(): ReadonlySignal<readonly VolumeProfileIncrementOption[]> {
        return this.#incrementOptions;
    }

    get overlays(): ReadonlySignal<Readonly<Record<VolumeProfileOverlay, boolean>>> {
        return this.#overlays;
    }

    get loading(): ReadonlySignal<boolean> {
        return this.#loading;
    }

    get error(): ReadonlySignal<string | null> {
        return this.#error;
    }

    /** Ladder rows, highest price first. */
    get levels(): ReadonlySignal<readonly VolumeProfileLevelView[]> {
        return this.#levels;
    }

    get stats(): ReadonlySignal<readonly VolumeProfileStatGroup[]> {
        return this.#stats;
    }

    get legend(): ReadonlySignal<readonly VolumeProfileLegendItem[]> {
        return this.#legend;
    }

    get rangeSubtitle(): ReadonlySignal<string> {
        return this.#rangeSubtitle;
    }

    get isEmpty(): ReadonlySignal<boolean> {
        return this.#isEmpty;
    }

    get chart(): ReadonlySignal<VolumeProfileChart | null> {
        return this.#chart;
    }

    get liveStatus(): ReadonlySignal<LiveBarStatus> {
        return this.#liveStatus;
    }

    /** The live stream's delivery profile and cadence, for developers only; `null` otherwise. */
    get liveProfileLabel(): ReadonlySignal<string | null> {
        return this.#liveProfileLabel;
    }

    get currentPrice(): ReadonlySignal<number | null> {
        return this.#lastPrice;
    }

    get vwap(): ReadonlySignal<number | null> {
        return this.#vwap;
    }

    get lastPriceFormatted(): ReadonlySignal<string> {
        return this.#lastPriceFormatted;
    }

    get vwapFormatted(): ReadonlySignal<string> {
        return this.#vwapFormatted;
    }

    setTab(tabId: VolumeProfileTabId): void {
        this.#dispatch(setTab(tabId));
    }

    setViewMode(viewMode: VolumeProfileViewMode): void {
        this.#dispatch(setViewMode(viewMode));
    }

    setSession(session: VolumeProfileSession): void {
        this.#dispatch(setSession(session));
    }

    setRange(range: VolumeProfileRange): void {
        this.#dispatch(setRange(range));
    }

    setIncrement(increment: VolumeProfileIncrement): void {
        this.#dispatch(setIncrement(increment));
    }

    /** Selects an increment by the id used in {@link incrementOptions}; unknown ids are ignored. */
    setIncrementById(id: string): void {
        const option = this.#incrementOptions.value.find((candidate) => candidate.id === id);
        if (option !== undefined) this.setIncrement(option.value);
    }

    toggleOverlay(overlay: VolumeProfileOverlay): void {
        this.#dispatch(toggleOverlay(overlay));
    }

    refetch(): void {
        void this.#query.queryObserver.refetch();
        void this.#summaryQuery.queryObserver.refetch();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}

export type {
    VolumeProfileLegendItem,
    VolumeProfileLevelView,
    VolumeProfileOverlay,
    VolumeProfileRange,
    VolumeProfileSession,
    VolumeProfileStatGroup,
    VolumeProfileTabId,
    VolumeProfileViewMode,
} from './state/types';
